import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Button } from "../ui/button"; // Assuming Button is in ui/components/ui/button

/**
 * Renders a single chat message with appropriate styling and markdown support.
 * 
 * @param {object} props - Component props.
 * @param {object} props.message - The message object to render. Expected properties:
 *   - author: 'user', 'ai', or 'system'
 *   - text: The message content (string, potentially markdown)
 *   - timestamp: Formatted time string
 *   - isCompleted: (Optional) Boolean, if true, applies opacity
 *   - isSystemMsg: (Optional) Boolean, applies system message styling
 *   - actions: (Optional) Array of action objects { label, action, variant? }
 */
export default function MessageItem({ message }) {
    // Determine styling based on message author and type
    const alignmentClass = message.author === 'user' ? 'justify-end' : 'justify-start';
    const bubbleColorClass = message.author === 'user' 
        ? 'bg-blue-600 text-white' 
        : message.author === 'system' 
            ? 'bg-gray-100 text-gray-700 border border-gray-300' 
            : 'bg-gray-200 text-gray-800';
    const opacityClass = message.isCompleted ? 'opacity-75' : '';
    const proseClass = `prose ${message.author === 'user' ? 'prose-invert' : ''} max-w-none ${message.isSystemMsg ? 'text-sm italic' : ''}`;
    const timestampColorClass = message.author === 'user' 
        ? 'text-blue-200' 
        : 'text-gray-500';
    const linkColorClass = message.author === 'user' ? 'text-blue-200' : 'text-blue-600';


    // Filter out empty or whitespace-only messages
    if (!message.text || !message.text.trim()) {
        return null; 
    }

    return (
        <div 
            className={`flex ${alignmentClass} message`}
        >
            <div className={`max-w-sm lg:max-w-lg p-3 rounded-lg ${bubbleColorClass} ${opacityClass} message-bubble`}>
                <ReactMarkdown
                    className={proseClass}
                    components={{
                        // Customize markdown elements for consistent styling within the chat bubble
                        p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                        ul: ({ node, ...props }) => <ul className="list-disc ml-4 mb-2" {...props} />,
                        ol: ({ node, ...props }) => <ol className="list-decimal ml-4 mb-2" {...props} />,
                        code: ({ node, inline, className, children, ...props }) => {
                            const match = /language-(\w+)/.exec(className || '');
                            return !inline ? (
                                <pre className="block bg-gray-800 text-gray-100 p-2 rounded overflow-x-auto my-2">
                                    <code className={`language-${match ? match[1] : 'text'}`} {...props}>
                                        {children}
                                    </code>
                                </pre>
                            ) : (
                                <code className="bg-gray-700 text-gray-100 px-1 rounded mx-0.5" {...props}>
                                    {children}
                                </code>
                            );
                        },
                        pre: ({ node, ...props }) => <div {...props} />, 
                        h1: ({ node, ...props }) => <h1 className="text-xl font-bold mb-2" {...props} />,
                        h2: ({ node, ...props }) => <h2 className="text-lg font-bold mb-2" {...props} />,
                        h3: ({ node, ...props }) => <h3 className="text-md font-bold mb-2" {...props} />,
                        a: ({ node, ...props }) => (
                            <a className={`${linkColorClass} hover:underline`} target="_blank" rel="noopener noreferrer" {...props} />
                        ),
                        blockquote: ({ node, ...props }) => (
                            <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2" {...props} />
                        ),
                        table: ({ node, ...props }) => (
                            <div className="overflow-x-auto my-2">
                                <table className="min-w-full divide-y divide-gray-300 border border-gray-300" {...props} />
                            </div>
                        ),
                        th: ({ node, ...props }) => (
                            <th className="px-3 py-2 bg-gray-100 font-semibold text-left border border-gray-300" {...props} />
                        ),
                        td: ({ node, ...props }) => (
                            <td className="px-3 py-2 border border-gray-300" {...props} />
                        ),
                    }}
                >
                    {message.text}
                </ReactMarkdown>
                <p className={`text-xs mt-1 ${timestampColorClass}`}>
                    {message.timestamp}
                </p>

                {/* Display action buttons if available */}
                {message.actions && message.actions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {message.actions.map((action, actionIndex) => (
                            <Button
                                key={actionIndex}
                                size="sm"
                                variant={action.variant || "secondary"}
                                onClick={action.action}
                                className="text-xs" // Keep button text small
                            >
                                {action.label}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}